import React, { memo } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ChevronLeft, ChevronRight, FileQuestion } from 'lucide-react';
import { getFileExtension } from '../../lib/fileTypes';
import Tip from '../ui/tip';

/**
 * PreviewRail — what the artifact surface becomes for a file that has no
 * renderable preview (`.json`, `.py`, `.css`, `.yaml`, …).
 *
 * Collapsing rather than unmounting is a deliberate trade. Removing the surface
 * entirely reclaims the most space, but the row then reflows every time you
 * click between a previewable and a non-previewable file — and Monaco
 * re-measures with it. A 48px rail keeps the row stable while still handing
 * ~570px back to the editor, and keeps the affordance visible so the preview
 * doesn't look broken or forgotten.
 *
 * The surface width itself is switched by a class in Workspace and never
 * animated: the sibling surface holds Monaco with automaticLayout, which would
 * re-measure on every frame of a width tween. Only the contents fade.
 */

const PreviewRail = memo(function PreviewRail({ file, collapsed, onToggle }) {
    const reduceMotion = useReducedMotion();
    const ext = getFileExtension(file?.name);

    const fade = reduceMotion
        ? {}
        : {
            initial: { opacity: 0 },
            animate: { opacity: 1 },
            transition: { duration: 0.18, ease: [0.32, 0.72, 0, 1] },
        };

    if (collapsed) {
        return (
            // `left`: the rail is pinned to the right edge of the window, so a
            // tooltip below it would hang off-screen.
            <Tip label="No preview for this file type — click for details" side="left">
                <motion.button
                    key="rail"
                    type="button"
                    onClick={onToggle}
                    data-testid="preview-rail-expand"
                    aria-label="No preview available. Show details."
                    className="group flex min-h-0 w-full flex-1 flex-col items-center gap-3 py-4 text-muted-foreground transition-colors hover:bg-white/[0.03] hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring"
                    {...fade}
                >
                    <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/5">
                        <FileQuestion className="h-4 w-4" />
                    </span>

                    <span className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] [writing-mode:vertical-rl] rotate-180">
                        No preview
                    </span>

                    <ChevronLeft className="mt-auto h-4 w-4 opacity-50 transition-opacity group-hover:opacity-100" />
                </motion.button>
            </Tip>
        );
    }

    return (
        <motion.div
            key="rail-detail"
            className="flex min-h-0 w-full flex-1 flex-col overflow-y-auto p-6"
            data-testid="preview-rail-detail"
            {...fade}
        >
            <div className="flex items-start justify-between gap-2">
                <span className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/30 to-accent/20 shadow-lg shadow-primary/10">
                    <FileQuestion className="h-6 w-6 text-primary" />
                </span>

                <Tip label="Collapse panel" side="left">
                    <button
                        type="button"
                        onClick={onToggle}
                        data-testid="preview-rail-collapse"
                        className="-mr-2 flex items-center justify-center rounded-lg p-2 text-muted-foreground transition-colors hover:bg-white/5 hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    >
                        <ChevronRight className="h-4 w-4" />
                    </button>
                </Tip>
            </div>

            <p className="mt-6 font-mono text-[10px] font-bold uppercase tracking-[0.28em] text-muted-foreground">
                No preview
            </p>

            <h2 className="mt-2 font-mono text-lg font-bold tracking-tight text-foreground">
                Nothing to render
            </h2>

            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                <span className="rounded-md border border-white/10 bg-white/5 px-1.5 py-0.5 font-mono text-[11px] uppercase text-foreground">
                    {ext || 'file'}
                </span>{' '}
                has no visual form to preview. Markdown, XML, mockups and images do — everything
                else lives in the editor.
            </p>

            <p className="mt-auto text-xs leading-relaxed text-muted-foreground/70">
                Editing continues to save normally.
            </p>
        </motion.div>
    );
});

export default PreviewRail;
